<template>
  <teleport to="body">
    <WdsModal
      :show="open"
      md
      :title="
        hire?.recruiterManagesWorkers
          ? $t('Manage Staffing Agency ownership')
          : $t('Attribution to a Staffing Agency')
      "
      @cancel="emit('close')"
    >
      <div v-if="loading" class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
        <WdsSpinner indeterminate />
      </div>

      <WdsStack v-else direction-column gap-md>
        <WdsText v-if="hire?.recruiterManagesWorkers">
          {{
            $t(
              "The staffing agency will manage the worker's contract and payments as the contracted party until the ownership period ends.",
            )
          }}
        </WdsText>
        <WdsText v-else>
          {{
            $t(
              "The staffing agency's fee will be added to invoices until the ownership period ends.",
            )
          }}
        </WdsText>

        <WdsStack direction-column gap-sm>
          <SelectAttributeRecruiter
            :active-recruiter="activeRecruiter"
            :company-recruiters="companyRecruiters"
            :change-fee="false"
            :change-ownership="true"
            hide-fee
            @update:recruiter-id="onRecruiterSelected"
            @update:ownership-days="ownershipDays = $event"
            @update:ownership-start-date="ownershipStartDate = $event"
            @update:ownership-never-expires="ownershipNeverExpires = $event"
          />

          <!-- Fee editor: rate + basis (percentage or fixed per unit), mirroring
               how the present-to-client flow edits fees. -->
          <WdsStack
            v-if="selectedRecruiterId && !hireManagesWorkers"
            direction-row
            align-end
            gap-sm
          >
            <WdsStack direction-column gap-custom="4px" class="tw-w-40">
              <WdsText small>{{ $t('Fee') }}</WdsText>
              <WdsSelect
                :start-selected="feeBasis"
                :list="feeBasisOptions"
                data-test="attribute-recruiter-fee-basis"
                @select-option="(opt) => (feeBasis = opt.value)"
              />
            </WdsStack>
            <div class="tw-w-40">
              <WdsInputCurrency
                v-model="feeRateInput"
                :config="feeInputConfig"
                data-test="attribute-recruiter-fee-rate"
              />
            </div>
          </WdsStack>
        </WdsStack>
      </WdsStack>

      <template #footer>
        <WdsStack v-if="!loading" justify-space-between gap-sm>
          <WdsStack gap-sm>
            <WdsButton @click="emit('close')">
              {{ $t('Cancel') }}
            </WdsButton>
          </WdsStack>

          <WdsStack gap-sm>
            <WdsButton
              v-if="activeRecruiter && hire?.canRemoveRecruiter && !hire?.recruiterManagesWorkers"
              error-subtle
              :loading="removeLoading"
              @click="handleRemove"
            >
              {{ $t('Remove attribution') }}
            </WdsButton>

            <WdsButton primary :loading="attributeLoading" :disabled="!canSave" @click="handleSave">
              {{ $t('Save') }}
            </WdsButton>
          </WdsStack>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import {
  WdsButton,
  WdsInputCurrency,
  WdsModal,
  WdsSelect,
  WdsSpinner,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'

import { graphql, useFragment } from '@/gql'
import { FeeApplication, FeeBasis, FeeType } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import useCurrency from '@/composables/useCurrency'
import { SelectAttributeRecruiter } from '@/app-bootstrapping/componentRegister'
import { Notification, NetworkError } from '@/utils/notification'
import useApolloError from '@/composables/useApolloError'
import queriesToRefetch from '@/components/Hire/queriesToRefetch'
import { PER_PAGE_MAXIMUM } from '@/utils/constants'

const props = withDefaults(
  defineProps<{
    open?: boolean
    hireId: string
  }>(),
  {
    open: false,
  },
)

const emit = defineEmits(['close', 'save'])

// Form state
const selectedRecruiterId = ref<string>('')
const feeBasis = ref<FeeBasis>(FeeBasis.Percentage)
const feeRateInput = ref<string>('')
const ownershipDays = ref<number | null>(null)
const ownershipStartDate = ref<string | null>(null)
const ownershipNeverExpires = ref<boolean>(false)

const HireAttributeRecruiterFragment = graphql(`
  fragment HireAttributeRecruiterFields on Hire {
    id
    recruiterManagesWorkers
    attributionManagesWorkers
    recruiterOwnershipDays
    recruiterOwnershipStartDate
    startDate
    canRemoveRecruiter
    currency
    rateType
    fees {
      id
      feeType
      application
      basis
      rate
      recipient {
        id
      }
    }
    company {
      id
      staffingAgencies(first: $first) {
        data {
          id
          recruiterOwnershipDays
          recruiterManagesWorkers
          fees {
            id
            feeType
            application
            basis
            rate
            recipient {
              id
            }
          }
          recruiter {
            id
            name
          }
        }
      }
    }
    recruiter {
      id
      name
    }
  }
`)

const GET_HIRE_FOR_ATTRIBUTE_RECRUITER = graphql(`
  query GetHireForAttributeRecruiter($id: ID!, $first: Int!) {
    hire(id: $id) {
      ...HireAttributeRecruiterFields
    }
  }
`)

const ATTRIBUTE_RECRUITER_TO_HIRE = graphql(`
  mutation AttributeRecruiterToHire($input: AttributeRecruiterToHireInput!, $first: Int!) {
    attributeRecruiterToHire(input: $input) {
      ...HireAttributeRecruiterFields
    }
  }
`)

const REMOVE_RECRUITER_FROM_HIRE = graphql(`
  mutation RemoveRecruiterFromHire($input: RemoveRecruiterFromHireInput!, $first: Int!) {
    removeRecruiterFromHire(input: $input) {
      ...HireAttributeRecruiterFields
    }
  }
`)

// The whole agency list feeds a plain <select> with no search, so it is fetched
// up front. Every operation spreading the fragment must pass the same `first`,
// or Apollo caches the list under a second field key and the query goes stale.
const { result, loading, refetch } = useQuery(
  GET_HIRE_FOR_ATTRIBUTE_RECRUITER,
  () => ({ id: props.hireId, first: PER_PAGE_MAXIMUM }),
  {
    clientId: 'publicApi',
  },
)

const {
  mutate: attributeRecruiter,
  loading: attributeLoading,
  error: attributeError,
} = useMutation(ATTRIBUTE_RECRUITER_TO_HIRE, {
  clientId: 'publicApi',
  refetchQueries: queriesToRefetch,
})

const {
  mutate: removeRecruiter,
  loading: removeLoading,
  error: removeError,
} = useMutation(REMOVE_RECRUITER_FROM_HIRE, {
  clientId: 'publicApi',
  refetchQueries: queriesToRefetch,
})

const { validationErrors: attributeValidationErrors } = useApolloError(attributeError)
const { validationErrors: removeValidationErrors } = useApolloError(removeError)

const hire = computed(() => useFragment(HireAttributeRecruiterFragment, result.value?.hire))

const currency = computed(() => hire.value?.currency ?? 'EUR')
const { config: currencyConfig } = useCurrency(currency)

// The amount input is reused for percentages, swapping the currency symbol
// for "%". Percentages are still capped at 100% via the Save guard below.
const feeInputConfig = computed(() =>
  feeBasis.value === FeeBasis.Percentage
    ? { ...currencyConfig.value, currencySymbol: '%' }
    : currencyConfig.value,
)

// A duration fee is only charged when its basis matches the hire's rate type.
const DURATION_FEE_BASIS_BY_RATE_TYPE: Record<string, FeeBasis> = {
  hourly: FeeBasis.Hourly,
  daily: FeeBasis.Daily,
  weekly: FeeBasis.Weekly,
  monthly: FeeBasis.Monthly,
}

const DURATION_OPTION_LABEL_BY_RATE_TYPE: Record<string, string> = {
  hourly: $t('Per hour'),
  daily: $t('Per day'),
  weekly: $t('Per week'),
  monthly: $t('Per month'),
}

// Public-API rateType is a plain string (e.g. "hourly"), not the RateType enum.
const rateType = computed(() => (hire.value?.rateType ?? '').toLowerCase())
const durationFeeBasis = computed<FeeBasis | null>(
  () => DURATION_FEE_BASIS_BY_RATE_TYPE[rateType.value] ?? null,
)

// Offer the matching per-unit option only for duration rate types; a fixed-rate
// hire can take only a percentage fee, so anything else would never be billed.
const feeBasisOptions = computed(() => {
  const options = [{ value: FeeBasis.Percentage, label: $t('Percentage') }]
  if (durationFeeBasis.value) {
    options.push({
      value: durationFeeBasis.value,
      label: DURATION_OPTION_LABEL_BY_RATE_TYPE[rateType.value] ?? $t('Per unit'),
    })
  }
  return options
})

// A hire can carry referral fees for several recipients (e.g. a supplier
// markup next to the agency fee), so only the fee owed to the given
// recruiter is that recruiter's current fee.
const referralFeeOf = (
  fees: ReadonlyArray<{ feeType: FeeType; application: FeeApplication; basis: FeeBasis; rate: unknown; recipient?: { id: string } | null }> | undefined | null,
  recipientId: string,
) =>
  (fees ?? []).find(
    (fee) =>
      fee.feeType === FeeType.Referral
      && fee.application === FeeApplication.Additive
      && fee.recipient?.id === recipientId,
  ) ?? null

// A stored rate is a decimal fraction for a percentage (0.05) and a monetary
// amount for a fixed fee (7.50). Show it in the units the user types.
const rateForInput = (rate: number, basis: FeeBasis) =>
  basis === FeeBasis.Percentage ? `${rate * 100}` : `${rate}`

const activeRecruiter = computed(() => {
  if (!hire.value?.recruiter) return undefined

  const fee = referralFeeOf(hire.value.fees, hire.value.recruiter.id)

  return {
    id: hire.value.recruiter.id,
    company: hire.value.recruiter.name,
    fee_basis: fee?.basis ?? FeeBasis.Percentage,
    fee_rate: fee ? Number(fee.rate) : 0,
    ownership_days: hire.value.recruiterOwnershipDays,
    // Fall back to the hire's contract start (the backend's own anchor), not
    // today; if there's no date to infer, leave it null for the user to pick.
    ownership_start_date: hire.value.recruiterOwnershipStartDate ?? hire.value.startDate,
    manages_workers: hire.value.recruiterManagesWorkers || false,
  }
})

const companyRecruiters = computed(() => {
  if (!hire.value?.company?.staffingAgencies?.data) return []

  return hire.value.company.staffingAgencies.data.map((staffingAgency) => {
    const fee = referralFeeOf(staffingAgency.fees, staffingAgency.recruiter.id)

    return {
      id: staffingAgency.recruiter.id,
      company: staffingAgency.recruiter.name,
      // Kept for the legacy percentage path in SelectAttributeRecruiter.
      fee: fee?.basis === FeeBasis.Percentage ? Number(fee.rate) : 0,
      fee_basis: fee?.basis ?? FeeBasis.Percentage,
      fee_rate: fee ? Number(fee.rate) : 0,
      ownership_days: staffingAgency.recruiterOwnershipDays,
      manages_workers: staffingAgency.recruiterManagesWorkers,
    }
  })
})

const recruiterById = (id: string) =>
  (activeRecruiter.value?.id === id ? activeRecruiter.value : null)
  ?? companyRecruiters.value.find((recruiter) => recruiter.id === id)
  ?? null

// Attribution keeps the hire's engagement type, so its flag (not the agency default) decides the fee.
const hireManagesWorkers = computed(
  () => hire.value?.attributionManagesWorkers ?? false,
)

const applyRecruiterFee = (recruiterId: string) => {
  const source = recruiterById(recruiterId)
  if (!source) return

  // Only prefill the rate when the stored fee's basis is one this hire can bill
  // (percentage, or its matching per-unit). A number from a different unit has no
  // meaning here, so reset to an empty percentage for the user to set.
  const storedBasis = source.fee_basis ?? FeeBasis.Percentage
  if (feeBasisOptions.value.some((option) => option.value === storedBasis)) {
    feeBasis.value = storedBasis
    feeRateInput.value = rateForInput(source.fee_rate ?? 0, storedBasis)
  } else {
    feeBasis.value = FeeBasis.Percentage
    feeRateInput.value = ''
  }
}

const onRecruiterSelected = (recruiterId: string) => {
  selectedRecruiterId.value = recruiterId
  applyRecruiterFee(recruiterId)
}

// Seed the form from the current attribution once the hire has loaded.
watch(
  activeRecruiter,
  (recruiter) => {
    if (!recruiter || selectedRecruiterId.value) return
    selectedRecruiterId.value = recruiter.id
    applyRecruiterFee(recruiter.id)
  },
  { immediate: true },
)

const canSave = computed(() => {
  if (!selectedRecruiterId.value) return false

  if (!hireManagesWorkers.value) {
    const rate = Number(feeRateInput.value)
    if (feeRateInput.value === '' || rate < 0) return false
    // WdsInputCurrency has no max, so cap percentages at 100% here.
    if (feeBasis.value === FeeBasis.Percentage && rate > 100) return false
  }

  return (ownershipNeverExpires.value || ownershipDays.value !== null) && ownershipStartDate.value !== null
})

const handleSave = async () => {
  if (!canSave.value) return

  // Percentage fees are stored as decimal fractions (5% -> 0.05).
  const rawRate = Number(feeRateInput.value)
  const feeRate = hireManagesWorkers.value
    ? null
    : {
        rate: feeBasis.value === FeeBasis.Percentage ? rawRate / 100 : rawRate,
        basis: feeBasis.value,
      }

  try {
    await attributeRecruiter({
      input: {
        hire: props.hireId,
        recruiter: selectedRecruiterId.value,
        feeRate,
        ownershipDays: ownershipNeverExpires.value ? null : ownershipDays.value!,
        ownershipStartDate: ownershipStartDate.value!,
      },
      first: PER_PAGE_MAXIMUM,
    })

    const successMessage = activeRecruiter.value
      ? $t(
          'The terms of the staffing agency attribution for this contract have successfully been changed.',
        )
      : $t('The contract is now attributed to the staffing agency.')

    new Notification({ msg: successMessage }).dispatch()

    await refetch()

    emit('save')
    emit('close')
  } catch (error: any) {
    const errorMessage =
      attributeValidationErrors.value?.['input.recruiter'] ||
      attributeValidationErrors.value?.['input.hire'] ||
      attributeValidationErrors.value?.['input.feeRate'] ||
      attributeValidationErrors.value?.['input.feeRate.rate'] ||
      attributeValidationErrors.value?.['input.fee'] ||
      attributeValidationErrors.value?.['input.ownershipDays'] ||
      attributeValidationErrors.value?.['input.ownershipStartDate'] ||
      $t('The contract could not be attributed')

    new NetworkError({
      msg: errorMessage,
      ex: error,
    }).dispatch()
  }
}

const handleRemove = async () => {
  if (!activeRecruiter.value) return

  try {
    await removeRecruiter({
      input: {
        hire: props.hireId,
      },
      first: PER_PAGE_MAXIMUM,
    })

    new Notification({
      msg: $t('The staffing agency has been removed from the contract.'),
    }).dispatch()

    emit('save')
    emit('close')
  } catch (error: any) {
    const errorMessage =
      removeValidationErrors.value?.['input.hire'] || $t('The staffing agency could not be removed')

    new NetworkError({
      msg: errorMessage,
      ex: error,
    }).dispatch()
  }
}
</script>
